import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaBrain, FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { paperApi } from '../utils/apiUtils';
import StreamingSummary from './StreamingSummary';

function AISummaryPage() {
  const { paperId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [paper, setPaper] = useState(null);
  const [paperLoading, setPaperLoading] = useState(true);
  const [paperError, setPaperError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) navigate('/sign-in');
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!paperId) return;
    let cancelled = false;
    setPaperLoading(true);
    setPaperError('');
    (async () => {
      try {
        const { paper: p } = await paperApi.fetchPaperById(paperId);
        if (!cancelled) setPaper(p);
      } catch (err) {
        if (!cancelled) setPaperError('Failed to load paper details');
      } finally {
        if (!cancelled) setPaperLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [paperId]);

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-yellow-300 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/30 text-xs">Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto py-4 sm:py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-4">
        <Link
          to="/ai"
          className="flex items-center gap-1.5 text-white/50 hover:text-white/90 text-xs transition-colors"
        >
          <FaArrowLeft className="text-[10px]" />
          AI Digest
        </Link>
        <span className="text-white/20 text-xs">/</span>
        <span className="text-white/80 text-xs font-semibold">Summary</span>
      </div>

      {/* Paper info card */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100 mb-5">
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />
        <div className="p-5">
          {paperLoading ? (
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin" />
              <span className="text-xs text-gray-400">
                Loading paper details...
              </span>
            </div>
          ) : paperError ? (
            <p className="text-xs text-red-500">{paperError}</p>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <FaBrain className="text-purple-400 text-xs flex-shrink-0" />
                  {paper?.topic && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-purple-500">
                      {paper.topic}
                    </span>
                  )}
                </div>
                <h1 className="text-lg font-bold text-gray-900 leading-snug mb-2">
                  {paper?.title || 'Paper Summary'}
                </h1>
                {paper?.first_author && (
                  <p className="text-xs text-gray-500">
                    {paper.first_author}
                  </p>
                )}
              </div>
              <Link
                to={`/paper/${paperId}`}
                className="flex items-center gap-1.5 text-[11px] text-purple-500 hover:text-purple-700 flex-shrink-0 font-semibold"
              >
                View paper
                <FaExternalLinkAlt className="text-[9px]" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Summary content */}
      <div className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100 p-6">
        <StreamingSummary paperId={paperId} />
      </div>
    </div>
  );
}

export default AISummaryPage;
